/**
 * Chart metrics (spec §9.6 "Metrics", §9.7 `chart`), computed client-side from the docs. Pure
 * functions returning a small table `{categories, series:[{name, data}]}` that the chart and its
 * table view both render.
 */
import { want, type TimelogEntry } from "../../core/computed";
import type { Task } from "../../core/schema";

export interface Table {
  categories: string[];
  series: { id: string; name: string; data: number[] }[];
  unit: string;
  kind: "stacked" | "bar" | "heatmap";
  /** categories are labels, not day keys */
  plainCategories?: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");
export const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function lastDays(now: number, n: number): Date[] {
  const out: Date[] = [];
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  for (let i = n - 1; i >= 0; i--) {
    // calendar days: subtracting 24-hour steps skipped a day after the spring clock change (bug hunt M12)
    const d = new Date(start);
    d.setDate(d.getDate() - i);
    out.push(d);
  }
  return out;
}

export const MAX_SERIES = 6;

/** Minutes per day, stacked by the task's first group; at most six groups, the rest fold into "other". */
export function timePerGroup(
  entries: TimelogEntry[],
  groupOf: (taskId: string) => string | null,
  groupTitle: (id: string) => string,
  now: number,
  days: number,
  labels: { none: string; other: string },
): Table {
  const dayList = lastDays(now, days);
  const idx = new Map(dayList.map((d, i) => [dayKey(d), i]));
  const per = new Map<string, number[]>();
  for (const e of entries) {
    const s = Date.parse(e.start);
    const f = e.end ? Date.parse(e.end) : now;
    if (!(f > s)) continue;
    const g = groupOf(e.taskId) ?? "";
    // split across midnight
    let cur = s;
    while (cur < f) {
      const d = new Date(cur);
      const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
      const end = Math.min(f, next);
      const i = idx.get(dayKey(d));
      if (i !== undefined) {
        if (!per.has(g)) per.set(g, new Array(dayList.length).fill(0));
        per.get(g)![i] += (end - cur) / 60_000;
      }
      cur = end;
    }
  }
  const ranked = [...per.entries()].sort(
    (a, b) => b[1].reduce((x, y) => x + y, 0) - a[1].reduce((x, y) => x + y, 0),
  );
  const keep = ranked.slice(0, MAX_SERIES);
  const rest = ranked.slice(MAX_SERIES);
  const series = keep.map(([g, data]) => ({
    id: g || "none",
    name: g ? groupTitle(g) : labels.none,
    data: data.map((v) => Math.round(v)),
  }));
  if (rest.length) {
    const sum = new Array(dayList.length).fill(0);
    for (const [, data] of rest) data.forEach((v, i) => (sum[i] += v));
    series.push({ id: "other", name: labels.other, data: sum.map((v) => Math.round(v)) });
  }
  return { categories: dayList.map(dayKey), series, unit: "min", kind: "stacked" };
}

function weekKey(d: Date): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // Monday
  return dayKey(x);
}

/** Completions per week: closed tasks plus recurring completions (skips are not completions). */
export function completions(tasks: Task[], now: number, weeks: number, label: string): Table {
  const keys: string[] = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - 7 * i); // calendar weeks back, not 7 × 24 hours (bug hunt M12)
    keys.push(weekKey(d));
  }
  const idx = new Map(keys.map((k, i) => [k, i]));
  const data = new Array(keys.length).fill(0);
  const bump = (iso: string) => {
    const i = idx.get(weekKey(new Date(iso)));
    if (i !== undefined) data[i] += 1;
  };
  for (const t of tasks) {
    if (t.doneAt) bump(t.doneAt);
    const h = t.props["rec.history"] as unknown;
    if (Array.isArray(h))
      for (const e of h as { at: string; skipped?: boolean }[]) if (e && !e.skipped && e.at) bump(e.at);
  }
  return { categories: keys, series: [{ id: "completions", name: label, data }], unit: "", kind: "bar" };
}

/** want of each recurring task at the end of each of the last `days` days, replayed from history. */
export function wantHeatmap(tasks: Task[], now: number, days: number): Table {
  const dayList = lastDays(now, days);
  const rec = tasks.filter((t) => t.typeId === "recurring" && !t.archivedAt).slice(0, 12);
  const series = rec.map((t) => {
    const h = (
      Array.isArray(t.props["rec.history"]) ? (t.props["rec.history"] as unknown as { at: string }[]) : []
    )
      .map((e) => e.at)
      .filter(Boolean)
      .sort();
    const data = dayList.map((d) => {
      const end = new Date(d);
      end.setDate(end.getDate() + 1);
      const at = Math.min(now, end.getTime() - 1);
      let last: string | null = null;
      for (const x of h) if (Date.parse(x) <= at) last = x;
      if (Date.parse(t.createdAt) > at) return 0;
      const w = want({ ...t, props: { ...t.props, "rec.lastDoneAt": last } } as never, at);
      return Math.round(Math.min(w, 3) * 100) / 100;
    });
    return { id: t.id, name: t.title, data };
  });
  return { categories: dayList.map(dayKey), series, unit: "", kind: "heatmap" };
}

export function counterSeries(
  entries: Record<string, number>,
  title: string,
  unit: string,
  now: number,
  days: number,
): Table {
  const dayList = lastDays(now, days);
  return {
    categories: dayList.map(dayKey),
    series: [{ id: "counter", name: title, data: dayList.map((d) => entries[dayKey(d)] ?? 0) }],
    unit,
    kind: "bar",
  };
}
